import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../components/ui/InputField';
import TextAreaField from '../components/ui/TextAreaField';
import Button from '../components/ui/Button';

const BakkalReportForm = ({ onDataChange }) => {
  const { t } = useTranslation();
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    contactInfo: '',
    address: ''
  });

  const [taxRate, setTaxRate] = useState(0); // Default 0 (no tax)

  const [items, setItems] = useState([
    { productName: '', quantity: 1, unitPrice: 0, total: 0 }
  ]);

  const [notes, setNotes] = useState('');

  useEffect(() => {
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const tax = taxRate > 0 ? subtotal * (taxRate / 100) : 0; // Calculate tax only if taxRate > 0
    const grandTotal = subtotal + tax;

    onDataChange({
      ...businessInfo,
      items,
      notes,
      subtotal,
      tax,
      taxRate, // Send tax rate
      grandTotal,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString()
    });
  }, [businessInfo, items, notes, taxRate, onDataChange]);

  const handleBusinessInfoChange = (e) => {
    setBusinessInfo({ ...businessInfo, [e.target.name]: e.target.value });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].total = newItems[index].quantity * newItems[index].unitPrice;
    }
    
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { productName: '', quantity: 1, unitPrice: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const tax = subtotal * 0.16;
  const grandTotal = subtotal + tax;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <InputField
          label={t('form.businessName')}
          name="businessName"
          value={businessInfo.businessName}
          onChange={handleBusinessInfoChange}
          placeholder="e.g., Bakaara Market"
          required
        />
        <InputField
          label={t('form.contactInfo')}
          name="contactInfo"
          value={businessInfo.contactInfo}
          onChange={handleBusinessInfoChange}
          placeholder="email@example.com / +252..."
        />
        <div className="md:col-span-2">
          <TextAreaField
            label={t('form.address')}
            name="address"
            value={businessInfo.address}
            onChange={handleBusinessInfoChange}
            placeholder="Enter your business address"
            rows={2}
          />
        </div>
        
        {/* NEW: Tax Rate Field */}
        <div>
          <InputField
            label="Tax Rate (%) - Optional"
            name="taxRate"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={taxRate}
            onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
            placeholder="0 (No Tax)"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Leave 0 for no tax. Example: 16 (Kenya), 18 (Turkey), 5 (UAE)
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Product Name</th>
              <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">{t('form.quantity')}</th>
              <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">{t('form.unitPrice')}</th>
              <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">{t('form.total')}</th>
              <th className="p-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                <td className="p-2">
                  <input
                    className="form-input w-full bg-transparent border-0 focus:ring-0 p-2"
                    type="text"
                    value={item.productName}
                    onChange={(e) => handleItemChange(index, 'productName', e.target.value)}
                    placeholder="e.g., Rice 25kg"
                  />
                </td>
                <td className="p-2">
                  <input
                    className="form-input w-20 bg-transparent border-0 focus:ring-0 p-2 text-center"
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td className="p-2">
                  <input
                    className="form-input w-28 bg-transparent border-0 focus:ring-0 p-2 text-right"
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td className="p-2 text-right font-medium text-[#111418] dark:text-gray-100">
                  ${item.total.toFixed(2)}
                </td>
                <td className="p-2 text-center">
                  <button
                    onClick={() => removeItem(index)}
                    className="text-gray-400 hover:text-red-500"
                    disabled={items.length === 1}
                  >
                    <span className="material-symbols-outlined text-xl">delete</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button onClick={addItem} variant="ghost" icon="add_circle" className="mt-4">
          {t('form.addRow')}
        </Button>
      </div>

      <div className="flex justify-end mt-6">
        <div className="w-full max-w-sm space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-gray-400">{t('form.subtotal')}</span>
            <span className="font-medium text-[#111418] dark:text-gray-100">${subtotal.toFixed(2)}</span>
          </div>
          
          {/* Show only if taxRate > 0 */}
          {taxRate > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">{t('form.tax')} ({taxRate}%)</span>
              <span className="font-medium text-[#111418] dark:text-gray-100">${tax.toFixed(2)}</span>
            </div>
          )}
          
          <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-lg font-bold text-[#111418] dark:text-gray-100">{t('form.grandTotal')}</span>
            <span className="text-lg font-bold text-primary">${grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <TextAreaField
          label={t('form.notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Payment is due within 30 days."
          rows={3}
        />
      </div>
    </div>
  );
};

export default BakkalReportForm;
